/* eslint-disable react-hooks/exhaustive-deps */
import { UploadOutlined, CopyOutlined } from "@ant-design/icons";
import { Modal, Form, Input, Button, Select, Upload, message } from "antd";
import { useState, useEffect } from "react";

import { copyTextToClipboard } from "../../../../../utils/clipboardHelper";

const { Option } = Select;

export const AuthorModal = ({
  authorModalOpen,
  setAuthorModalOpen,
  authorForm,
  submitBlogAuthor,
  uploadProfilePicture,
  isUploadingProfilePicture,
  profilePictureUrl,
  referenceData = { authors: [], educators: [], mentors: [] },
  isEditMode = false
}) => {
  const AUTHOR_TYPES = {
    EXTERNAL_AUTHOR: "EXTERNAL_AUTHOR",
    COURSE_EDUCATOR: "COURSE_EDUCATOR",
    DISCORD_MENTOR: "DISCORD_MENTOR"
  };

  const [selectedType, setSelectedType] = useState(null);
  const [referenceOptions, setReferenceOptions] = useState([]);

  useEffect(() => {
    switch (selectedType) {
      case AUTHOR_TYPES.COURSE_EDUCATOR:
        setReferenceOptions(referenceData.educators || []);
        break;
      case AUTHOR_TYPES.DISCORD_MENTOR:
        setReferenceOptions(referenceData.mentors || []);
        break;
      default:
        setReferenceOptions([]);
        break;
    }
  }, [selectedType, referenceData]);

  const formatAuthorTypeLabel = (key) => {
    return key
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const copyUrl = async () => {
    if (!profilePictureUrl) {
      return;
    }
    const hasCopied = await copyTextToClipboard(profilePictureUrl);
    if (!hasCopied) {
      message.error("Failed to copy profile URL");
      return;
    }
    message.success("Profile URL copied!");
  };

  return (
    <Modal
      open={authorModalOpen}
      title={isEditMode ? "Edit Author" : "Add New Author"}
      onCancel={() => setAuthorModalOpen(false)}
      footer={null}
    >
      <Form layout="vertical" form={authorForm} onFinish={(values) => submitBlogAuthor(values)}>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input placeholder="John Doe" />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Please enter a valid email" }
          ]}
        >
          <Input placeholder="john.doe@gmail.com" />
        </Form.Item>

        <Form.Item name="codeforcesHandle" label="Codeforces Handle">
          <Input placeholder="john_doe_cf" />
        </Form.Item>

        <Form.Item
          name="linkedinId"
          label="LinkedIn URL"
          rules={[
            { required: true, message: "LinkedIn URL required" },
            { type: "url", message: "Please enter a valid LinkedIn URL" }
          ]}
        >
          <Input placeholder="https://www.linkedin.com/in/username" />
        </Form.Item>

        <Form.Item
          name="twitterProfileUrl"
          label="Twitter/X URL"
          rules={[{ type: "url", message: "Please enter a valid Twitter/X URL" }]}
        >
          <Input placeholder="https://twitter.com/username" />
        </Form.Item>

        <Form.Item
          name="instagramProfileUrl"
          label="Instagram URL"
          rules={[{ type: "url", message: "Please enter a valid Instagram URL" }]}
        >
          <Input placeholder="https://instagram.com/username" />
        </Form.Item>

        <Form.Item
          name="type"
          label="Author Type"
          rules={[{ required: true, message: "Select author type" }]}
        >
          <Select
            placeholder="Select author type"
            onChange={(value) => setSelectedType(value)}
            disabled={isEditMode}
          >
            {Object.keys(AUTHOR_TYPES).map((key) => (
              <Option key={key} value={key}>
                {formatAuthorTypeLabel(key)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {referenceOptions.length > 0 && (
          <Form.Item
            name="authorReferenceId"
            label={`Select ${
              selectedType === AUTHOR_TYPES.COURSE_EDUCATOR ? "Educator" : "Mentor"
            }`}
            rules={[{ required: true, message: "Please select a reference" }]}
          >
            <Select
              placeholder={`Select ${
                selectedType === AUTHOR_TYPES.COURSE_EDUCATOR ? "Educator" : "Mentor"
              }`}
            >
              {referenceOptions.map((ref) => (
                <Option key={ref._id} value={ref._id}>
                  {ref.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="profilePictureUrl"
          label="Profile Picture URL"
          rules={[{ required: true, message: "Profile picture is required" }]}
        >
          <Input
            placeholder="Uploaded image URL will appear here"
            value={profilePictureUrl}
            readOnly
            addonAfter={
              profilePictureUrl ? (
                <Button type="link" icon={<CopyOutlined />} onClick={copyUrl} />
              ) : null
            }
          />
        </Form.Item>

        <Upload
          beforeUpload={uploadProfilePicture}
          multiple={false}
          showUploadList={false}
          accept="image/*"
        >
          <Button icon={<UploadOutlined />} loading={isUploadingProfilePicture}>
            Upload Profile Picture
          </Button>
        </Upload>

        {profilePictureUrl && (
          <div style={{ marginTop: 12, textAlign: "center" }}>
            <img
              src={profilePictureUrl}
              alt="Profile"
              style={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #ddd"
              }}
            />
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
          <Button onClick={() => setAuthorModalOpen(false)} style={{ marginRight: 10 }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            {isEditMode ? "Save Changes" : "Add Author"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
